package com.evoapps.evoshift.ui.login

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.widgets.rememberStableText
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.adaptive.focusRing
import com.evoapps.evoshift.ui.adaptive.WindowHeightClass
import com.evoapps.evoshift.ui.adaptive.WindowWidthClass
import com.evoapps.evoshift.ui.legal.SignUpLegalNote
import com.evoapps.evoshift.widgets.EvoTextField
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.DialogWidth
import com.evoapps.evoshift.widgets.DialogAction
import androidx.compose.material.icons.filled.LockReset
import androidx.compose.foundation.text.KeyboardActions
import com.evoapps.evoshift.ui.util.asString
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.autofill.ContentType
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentType
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.*
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.ui.*
import com.evoapps.evoshift.viewmodels.AuthEvent
import com.evoapps.evoshift.viewmodels.AuthViewModel
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.LocalOnColours
import com.evoapps.evoshift.widgets.GradientBox
import com.evoapps.evoshift.widgets.StandoutCard
import evoshift.composeapp.generated.resources.*
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/** Test tags of the signed-out screen (SH-246). */
object AuthTags {
    /** The brand side of the wide layout: only on wide windows. */
    const val BRAND_PANE = "auth_brand_pane"
    const val EMAIL = "auth_email"
    const val PASSWORD = "auth_password"
}

/**
 * SH-246: the sizes of the form. Wide windows (a laptop's 800 dp high) show the whole form, the
 * Google and Apple buttons and the legal note without scrolling: a little tighter there.
 */
@Immutable
private data class AuthMetrics(val spacing: Dp, val cardPadding: Dp, val buttonHeight: Dp, val sectionGap: Dp)

private val PhoneMetrics = AuthMetrics(spacing = 16.dp, cardPadding = 24.dp, buttonHeight = 56.dp, sectionGap = 32.dp)
private val WideMetrics = AuthMetrics(spacing = 12.dp, cardPadding = 20.dp, buttonHeight = 48.dp, sectionGap = 16.dp)

/** The form's column on a wide window, beside the brand side. */
private val WideFormWidth = 440.dp

/** The two panes together on a wide window; wider monitors leave the rest to the gradient. */
private val WideContentWidth = 1120.dp

/**
 * Two panes from the expanded width class (840 dp: a laptop, a desktop browser, a tablet in
 * landscape) unless the window is very short (a phone in landscape), which keeps the column.
 */
internal fun authWideLayout(width: Dp, height: Dp): Boolean =
    WindowWidthClass.fromWidth(width).isExpanded && !WindowHeightClass.fromHeight(height).isCompact

@Composable
fun AuthScreen(
    viewModel: AuthViewModel,
    headerContent: @Composable (() -> Unit)? = null,
    onNavigateToDashboard: () -> Unit
) {
    // Obserwowanie stanu z ViewModel
    val state by viewModel.uiState.collectAsStateWithLifecycle()

    // Lokalne stany dla pól tekstowych
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var displayName by remember { mutableStateOf("") }
    var isPasswordVisible by remember { mutableStateOf(false) }
    var showForgotPasswordDialog by remember { mutableStateOf(false) }

    // SH-246: Enter moves from the e-mail to the password; Enter in the password submits.
    val emailFocus = remember { FocusRequester() }
    val passwordFocus = remember { FocusRequester() }
    val submit: () -> Unit = {
        if (state.isLoginMode) {
            viewModel.onEvent(AuthEvent.Login(email, password))
        } else {
            viewModel.onEvent(AuthEvent.Register(email, password, displayName))
        }
    }

    // Reakcja na sukces logowania (Side Effect)
    LaunchedEffect(state.authSuccess) {
        if (state.authSuccess) {
            onNavigateToDashboard()
        }
    }

    // Wyświetlanie błędów (Snackbar)
    val snackbarHostState = remember { SnackbarHostState() }
    val errorText = state.error?.asString()
    LaunchedEffect(errorText) {
        errorText?.let { error ->
            snackbarHostState.showSnackbar(error)
            viewModel.onEvent(AuthEvent.ErrorShown)
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = Color.Transparent // We'll use a custom background
    ) { padding ->
        // SH-245: what is drawn straight on the gradient takes its light roles.
        GradientBox(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding),
            contentAlignment = Alignment.Center
        ) {
            BoxWithConstraints(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                val wide = authWideLayout(maxWidth, maxHeight)
                val metrics = if (wide) WideMetrics else PhoneMetrics
                val screenHeight = maxHeight

                // SH-246: the cursor in the e-mail field at a keyboard (desktop, a computer's browser).
                val focusOnStart = focusesFirstFieldOnStart(wide)
                LaunchedEffect(focusOnStart, state.requiresEmailVerification) {
                    if (focusOnStart && !state.requiresEmailVerification) runCatching { emailFocus.requestFocus() }
                }

                val verificationCard: @Composable (Modifier) -> Unit = { modifier ->
                    VerifyEmailCard(
                        metrics = metrics,
                        modifier = modifier,
                        onVerified = { viewModel.onEvent(AuthEvent.CheckEmailVerification) },
                        onResend = { viewModel.onEvent(AuthEvent.ResendVerificationEmail) },
                        onCancel = { viewModel.onEvent(AuthEvent.CancelVerification) }
                    )
                }

                val formCard: @Composable () -> Unit = {
                    StandoutCard(modifier = Modifier.fillMaxWidth()) {
                        Column(
                            modifier = Modifier.padding(metrics.cardPadding),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(metrics.spacing)
                        ) {
                            Text(
                                text = if (state.isLoginMode) stringResource(Res.string.auth_login_title) else stringResource(Res.string.auth_register_title),
                                style = MaterialTheme.typography.headlineSmall,
                                fontWeight = FontWeight.Bold,
                                color = LightGold,
                                textAlign = TextAlign.Center
                            )

                            // Pole Imię (tylko przy rejestracji)
                            if (!state.isLoginMode) {
                                AuthTextField(
                                    value = displayName,
                                    onValueChange = { displayName = it },
                                    label = stringResource(Res.string.auth_full_name),
                                    leadingIcon = Icons.Default.Person,
                                    contentType = ContentType.PersonFullName,
                                    capitalization = KeyboardCapitalization.Words,
                                    imeAction = ImeAction.Next,
                                    keyboardActions = KeyboardActions(onNext = { emailFocus.requestFocus() }),
                                    onEnter = { emailFocus.requestFocus() }
                                )
                            }

                            // Pole Email: the account's name for password managers (a new one when registering).
                            AuthTextField(
                                value = email,
                                onValueChange = { email = it },
                                label = stringResource(Res.string.auth_email),
                                leadingIcon = Icons.Default.Email,
                                modifier = Modifier.focusRequester(emailFocus).testTag(AuthTags.EMAIL),
                                contentType = if (state.isLoginMode) {
                                    ContentType.Username + ContentType.EmailAddress
                                } else {
                                    ContentType.NewUsername + ContentType.EmailAddress
                                },
                                keyboardType = KeyboardType.Email,
                                imeAction = ImeAction.Next,
                                keyboardActions = KeyboardActions(onNext = { passwordFocus.requestFocus() }),
                                onEnter = { passwordFocus.requestFocus() }
                            )

                            // Pole Hasło
                            AuthTextField(
                                value = password,
                                onValueChange = { password = it },
                                label = stringResource(Res.string.auth_password),
                                leadingIcon = Icons.Default.Lock,
                                modifier = Modifier.focusRequester(passwordFocus).testTag(AuthTags.PASSWORD),
                                contentType = if (state.isLoginMode) ContentType.Password else ContentType.NewPassword,
                                isPassword = true,
                                isPasswordVisible = isPasswordVisible,
                                onPasswordVisibilityToggle = { isPasswordVisible = !isPasswordVisible },
                                keyboardType = KeyboardType.Password,
                                imeAction = ImeAction.Done,
                                keyboardActions = KeyboardActions(onDone = { submit() }),
                                onEnter = submit
                            )

                            if (!wide) Spacer(Modifier.height(8.dp))

                            // Przycisk akcji (Sign In / Register)
                            GoldButton(
                                text = if (state.isLoginMode) stringResource(Res.string.auth_sign_in) else stringResource(Res.string.auth_sign_up),
                                height = metrics.buttonHeight,
                                fontSize = 18.sp,
                                onClick = submit
                            )

                            // Opcje dodatkowe
                            if (state.isLoginMode) {
                                TextButton(onClick = { showForgotPasswordDialog = true }) {
                                    Text(stringResource(Res.string.auth_forgot_password), color = LocalOnColours.current.link)
                                }
                            }

                            OrDivider()

                            // Sign in with Apple (SH-92, App Store guideline 4.8): at least as
                            // prominent as Google, so first and the same size.
                            if (state.appleSignInAvailable) {
                                AppleButton(height = metrics.buttonHeight, onClick = { viewModel.onEvent(AuthEvent.ContinueWithApple) })
                            }
                            // Logowanie i rejestracja kontem Google (pierwsze logowanie zakłada konto)
                            GoogleButton(height = metrics.buttonHeight, onClick = { viewModel.onEvent(AuthEvent.ContinueWithGoogle) })
                            // Signing up (also with Google) accepts the terms; the version is recorded (SH-50).
                            SignUpLegalNote(color = LocalOnColours.current.onSurfaceVariant)
                        }
                    }
                }

                val switchMode: @Composable () -> Unit = {
                    SwitchModeSection(
                        isLoginMode = state.isLoginMode,
                        metrics = metrics,
                        // The tablet pairing and the language sit on the brand side of a wide window.
                        showTools = !wide,
                        onToggle = { viewModel.onEvent(AuthEvent.ToggleMode) }
                    )
                }

                if (wide) {
                    // SH-246: the brand and what EVoShift does on the left, the form on the right,
                    // all of it on one laptop screen. Scrolls only in a window shorter than that.
                    Row(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(rememberScrollState())
                            .contentWidth(WideContentWidth)
                            .heightIn(min = screenHeight)
                            .padding(horizontal = 48.dp, vertical = 24.dp),
                        horizontalArrangement = Arrangement.spacedBy(56.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        BrandPane(
                            headerContent = headerContent,
                            showTools = !state.requiresEmailVerification,
                            modifier = Modifier.weight(1f)
                        )
                        Column(
                            modifier = Modifier.width(WideFormWidth),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            if (state.requiresEmailVerification) {
                                verificationCard(Modifier.fillMaxWidth())
                            } else {
                                formCard()
                                Spacer(Modifier.height(metrics.sectionGap))
                                switchMode()
                            }
                        }
                    }
                } else if (state.requiresEmailVerification) {
                    // EKRAN WERYFIKACJI EMAIL
                    verificationCard(Modifier.padding(24.dp))
                } else {
                    // EKRAN LOGOWANIA / REJESTRACJI
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .verticalScroll(rememberScrollState())
                            .contentWidth(FormMaxWidth) // phones: full width; tablets in portrait: a centred form
                            .padding(24.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center
                    ) {
                        if (headerContent != null) {
                            headerContent()
                        } else {
                            Image(
                                painter = painterResource(Res.drawable.app_logo),
                                contentDescription = stringResource(Res.string.app_name),
                                modifier = Modifier.size(120.dp)
                            )
                        }
                        Spacer(Modifier.height(metrics.sectionGap))
                        formCard()
                        Spacer(Modifier.height(metrics.sectionGap))
                        switchMode()
                    }
                }
            }

            if (showForgotPasswordDialog) {
                ForgotPasswordDialog(
                    initialEmail = email,
                    onDismiss = { showForgotPasswordDialog = false },
                    onConfirm = { resetEmail ->
                        viewModel.onEvent(AuthEvent.ForgotPassword(resetEmail))
                        showForgotPasswordDialog = false
                    }
                )
            }

            // Nakładka ładowania
            LoadingOverlay(isLoading = state.isLoading)
        }
    }
}

/**
 * SH-246: the left side of a wide window. The logo and the promise with three things EVoShift
 * does (from the store listing), or the invitation's header when a link waits for the account;
 * below them pairing a workplace tablet and the language.
 */
@Composable
private fun BrandPane(
    headerContent: @Composable (() -> Unit)?,
    showTools: Boolean,
    modifier: Modifier = Modifier
) {
    val on = LocalOnColours.current
    Column(
        modifier = modifier.testTag(AuthTags.BRAND_PANE),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(24.dp)
    ) {
        if (headerContent != null) {
            headerContent()
        } else {
            Image(
                painter = painterResource(Res.drawable.app_logo),
                contentDescription = stringResource(Res.string.app_name),
                modifier = Modifier.size(112.dp)
            )
            Text(
                stringResource(Res.string.auth_brand_promise),
                style = MaterialTheme.typography.headlineLarge,
                fontWeight = FontWeight.Bold,
                color = on.accent,
                textAlign = TextAlign.Center
            )
            Column(
                modifier = Modifier.widthIn(max = 460.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                Benefit(Icons.Default.CalendarMonth, stringResource(Res.string.auth_benefit_roster))
                Benefit(Icons.Default.SwapHoriz, stringResource(Res.string.auth_benefit_requests))
                Benefit(Icons.Default.Insights, stringResource(Res.string.auth_benefit_costs))
            }
        }
        if (showTools) {
            // One line where it fits, else the language under the pairing (a 1024 dp window).
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally),
                itemVerticalAlignment = Alignment.CenterVertically
            ) {
                // SH-107: a workplace tablet signs in with a manager's one-time code.
                com.evoapps.evoshift.ui.kiosk.KioskPairButton()
                // SH-137: the language, also before having an account.
                com.evoapps.evoshift.ui.user.LanguageButton(viewModel = com.evoapps.evoshift.di.diViewModel())
            }
        }
    }
}

@Composable
private fun Benefit(icon: ImageVector, text: String) {
    val on = LocalOnColours.current
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(16.dp)) {
        Icon(icon, contentDescription = null, tint = on.accent, modifier = Modifier.size(28.dp))
        Text(text, style = MaterialTheme.typography.bodyLarge, color = on.onSurface)
    }
}

/** "New to EVoShift?" and Create account, or back to signing in; on a phone also the tablet pairing and the language. */
@Composable
private fun SwitchModeSection(isLoginMode: Boolean, metrics: AuthMetrics, showTools: Boolean, onToggle: () -> Unit) {
    val pill = RoundedCornerShape(28.dp)
    if (isLoginMode) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.padding(top = if (showTools) 24.dp else 0.dp)
        ) {
            Text(
                stringResource(Res.string.auth_new_to_evoshift),
                color = LocalOnColours.current.onSurfaceVariant,
                style = MaterialTheme.typography.bodyMedium
            )
            Spacer(Modifier.height(if (showTools) 16.dp else 8.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(metrics.buttonHeight)
                    .focusRing(pill)
                    .border(1.dp, GoldMetallicBrush, pill) // Pill shape
                    .clip(pill)
                    .handCursor().clickable(onClick = onToggle),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    stringResource(Res.string.auth_switch_to_register),
                    color = LightGold,
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp
                )
            }
            if (showTools) {
                // SH-107: a workplace tablet signs in with a manager's one-time code.
                Spacer(Modifier.height(16.dp))
                com.evoapps.evoshift.ui.kiosk.KioskPairButton()
            }
        }
    } else {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(metrics.buttonHeight)
                .focusRing(pill)
                .border(1.dp, LightGold.copy(alpha = 0.5f), pill)
                .clip(pill)
                .handCursor().clickable(onClick = onToggle),
            contentAlignment = Alignment.Center
        ) {
            Text(
                stringResource(Res.string.auth_switch_to_login),
                color = LightGold,
                fontWeight = FontWeight.Bold
            )
        }
    }
    if (showTools) {
        // SH-137: the language, also before having an account.
        Spacer(Modifier.height(8.dp))
        com.evoapps.evoshift.ui.user.LanguageButton(viewModel = com.evoapps.evoshift.di.diViewModel())
    }
}

/** After registering: check the inbox, "I have verified", send again, back to the form. */
@Composable
private fun VerifyEmailCard(
    metrics: AuthMetrics,
    modifier: Modifier,
    onVerified: () -> Unit,
    onResend: () -> Unit,
    onCancel: () -> Unit
) {
    StandoutCard(modifier = modifier) {
        Column(
            modifier = Modifier.padding(metrics.cardPadding),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(metrics.spacing)
        ) {
            Icon(
                imageVector = Icons.Default.Email,
                contentDescription = null,
                modifier = Modifier.padding(bottom = 16.dp).size(48.dp),
                tint = DarkGold
            )

            Text(
                text = stringResource(Res.string.auth_verify_email_title),
                style = MaterialTheme.typography.headlineMedium,
                color = LightGold,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center
            )

            Text(
                text = stringResource(Res.string.auth_verify_email_desc),
                style = MaterialTheme.typography.bodyMedium,
                color = LocalOnColours.current.onSurfaceVariant,
                textAlign = TextAlign.Center
            )

            GoldButton(
                text = stringResource(Res.string.auth_i_have_verified),
                height = 50.dp,
                onClick = onVerified
            )

            TextButton(onClick = onResend, modifier = Modifier.fillMaxWidth()) {
                Text(stringResource(Res.string.auth_resend_verification), color = DarkGold)
            }

            TextButton(onClick = onCancel, modifier = Modifier.fillMaxWidth()) {
                Text(stringResource(Res.string.auth_return_to_login), color = LocalOnColours.current.onSurfaceVariant)
            }
        }
    }
}

/** The gold call to action (Sign in, Sign up, I have verified), with the keyboard focus ring (SH-246). */
@Composable
private fun GoldButton(text: String, height: Dp, onClick: () -> Unit, fontSize: androidx.compose.ui.unit.TextUnit = androidx.compose.ui.unit.TextUnit.Unspecified) {
    val shape = RoundedCornerShape(12.dp)
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(height)
            .focusRing(shape)
            .clip(shape)
            .background(GoldMetallicBrush)
            .handCursor().clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Text(text, color = DeepNavy, fontWeight = FontWeight.Bold, fontSize = fontSize)
    }
}

@Composable
private fun OrDivider() {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
        HorizontalDivider(Modifier.weight(1f), color = LightGold.copy(alpha = 0.25f))
        Text(
            stringResource(Res.string.auth_or),
            color = LocalOnColours.current.onSurfaceVariant,
            style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.padding(horizontal = 12.dp)
        )
        HorizontalDivider(Modifier.weight(1f), color = LightGold.copy(alpha = 0.25f))
    }
}

/**
 * Apple's "Sign in with Apple" button in the white style of the Human Interface Guidelines (for
 * dark backgrounds): white, the black Apple logo, a black label; as large as the Google button.
 */
@Composable
private fun AppleButton(height: Dp, onClick: () -> Unit) {
    val shape = RoundedCornerShape(12.dp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(height)
            .focusRing(shape)
            .clip(shape)
            .background(Color.White)
            .handCursor().clickable(onClick = onClick),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(painterResource(Res.drawable.ic_apple), contentDescription = null, modifier = Modifier.size(20.dp))
        Spacer(Modifier.width(10.dp))
        Text(
            stringResource(Res.string.auth_sign_in_with_apple),
            color = Color.Black,
            fontWeight = FontWeight.SemiBold,
            fontSize = 16.sp
        )
    }
}

/** Google's light sign-in button: white, the "G" mark, dark label. */
@Composable
private fun GoogleButton(height: Dp, onClick: () -> Unit) {
    val shape = RoundedCornerShape(12.dp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(height)
            .focusRing(shape)
            .clip(shape)
            .background(Color.White)
            .handCursor().clickable(onClick = onClick),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Image(painterResource(Res.drawable.ic_google), contentDescription = null, modifier = Modifier.size(20.dp))
        Spacer(Modifier.width(12.dp))
        Text(
            stringResource(Res.string.auth_continue_with_google),
            color = Color(0xFF1F1F1F),
            fontWeight = FontWeight.SemiBold,
            fontSize = 16.sp
        )
    }
}

@Composable
fun ForgotPasswordDialog(
    initialEmail: String,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit
) {
    var email by remember { mutableStateOf(initialEmail) }
    val submit = { if (email.isNotBlank()) onConfirm(email.trim()) }

    EvoDialog(
        title = stringResource(Res.string.auth_reset_password_title),
        icon = Icons.Default.LockReset,
        onDismiss = onDismiss,
        width = DialogWidth.Compact,
        confirm = DialogAction(stringResource(Res.string.auth_reset_button), submit, enabled = email.isNotBlank())
    ) {
        Text(
            text = stringResource(Res.string.auth_reset_password_desc),
            style = MaterialTheme.typography.bodyMedium,
            color = Color.White.copy(alpha = 0.7f)
        )
        EvoTextField(
            value = email,
            onValueChange = { email = it },
            label = stringResource(Res.string.auth_email),
            leadingIcon = Icons.Default.Email,
            keyboardType = KeyboardType.Email,
            imeAction = ImeAction.Done,
            keyboardActions = KeyboardActions(onDone = { submit() })
        )
    }
}

/**
 * A field of the sign-in form. SH-246: [contentType] tells password managers and the system's
 * autofill what the field holds (where the platform reads it: docs/desktop.md, "The sign-in screen on laptops"), no
 * autocorrect on addresses and passwords, and [onEnter] for a hardware Enter (desktop, the web),
 * which a single-line field does not turn into its IME action everywhere.
 */
@Composable
fun AuthTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    leadingIcon: ImageVector,
    modifier: Modifier = Modifier,
    contentType: ContentType? = null,
    isPassword: Boolean = false,
    isPasswordVisible: Boolean = false,
    onPasswordVisibilityToggle: (() -> Unit)? = null,
    keyboardType: KeyboardType = KeyboardType.Text,
    imeAction: ImeAction = ImeAction.Default,
    capitalization: KeyboardCapitalization = KeyboardCapitalization.None,
    keyboardActions: KeyboardActions = KeyboardActions.Default,
    onEnter: (() -> Unit)? = null
) {
    val enter by rememberUpdatedState(onEnter)
    rememberStableText(value, onValueChange).let { stable ->
        OutlinedTextField(
            value = stable.value,
            onValueChange = stable.onValueChange,
            label = { Text(label) },
            leadingIcon = { Icon(leadingIcon, contentDescription = null) },
            trailingIcon = if (isPassword && onPasswordVisibilityToggle != null) {
                {
                    IconButtonWithTooltip(onClick = onPasswordVisibilityToggle, tooltip = stringResource(Res.string.auth_toggle_password_visibility)) {
                        Icon(
                            if (isPasswordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff,
                            contentDescription = stringResource(Res.string.auth_toggle_password_visibility)
                        )
                    }
                }
            } else null,
            visualTransformation = if (isPassword && !isPasswordVisible) PasswordVisualTransformation() else VisualTransformation.None,
            modifier = modifier
                .fillMaxWidth()
                .then(if (contentType != null) Modifier.semantics { this.contentType = contentType } else Modifier)
                .onPreviewKeyEvent { event ->
                    val action = enter
                    if (action == null || (event.key != Key.Enter && event.key != Key.NumPadEnter)) return@onPreviewKeyEvent false
                    if (event.type == KeyEventType.KeyDown) action()
                    true
                },
            keyboardOptions = KeyboardOptions(
                capitalization = capitalization,
                autoCorrectEnabled = false,
                keyboardType = keyboardType,
                imeAction = imeAction
            ),
            keyboardActions = keyboardActions,
            singleLine = true,
            colors = OutlinedTextFieldDefaults.colors(
                focusedBorderColor = DarkGold,
                unfocusedBorderColor = Color.White.copy(alpha = 0.3f),
                focusedLabelColor = DarkGold,
                unfocusedLabelColor = Color.White.copy(alpha = 0.7f),
                focusedTextColor = Color.White,
                unfocusedTextColor = Color.White,
                focusedLeadingIconColor = DarkGold,
                unfocusedLeadingIconColor = Color.White.copy(alpha = 0.5f),
                focusedTrailingIconColor = DarkGold,
                unfocusedTrailingIconColor = Color.White.copy(alpha = 0.5f),
                cursorColor = DarkGold
            )
        )
    }
}
